import {
  useGetV2InmailMessageClassifyQuery,
  useGetV2InmailMessageUnReadCountQuery,
  usePostV2InmailMessageStatusMutation,
  usePostV2InmailMessageListMutation,
} from 'core/query/yapi/hk-web/InmailNotice/query'
import { BizType, InstType, ModuleCode, SubmoduleCode } from 'core/service/yapi/hk-web/InmailNotice/api'
import { useAuthenticate } from 'core/components/Authenticate'
import { useGetRewardTasksListQuery } from 'core/query/yapi/hk-web/Reward/query'
import { useGetV1PerpetualFollowsPublicTraderCollectQuery } from 'core/query/yapi/swap/SpecContractCopyTradeSystemV1Contreller/query'
import { useState, useMemo, useEffect, useRef } from 'react'
import { NO_LINK_TYPES, LABEL_LIST, NAV_LIST } from './constants'
import { TPostV2InmailMessageListResponse } from 'core/service/yapi/hk-web/InmailNotice/api'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery, useQueryClient, InfiniteData } from '@tanstack/react-query'
import { getV3GetOneTradeInfo } from 'core/service/yapi/hk-web/TradeWsApi/api'
import { getLangFromUrl } from 'core/utils'

// 类型定义
type NotifyMessage = TPostV2InmailMessageListResponse['data'][number]

interface PageData {
  data: NotifyMessage[]
  totalCount: number
  pageNum: number
  pageSize: number
  nextPage: number
}

type TNotifyParams = {
  navMaps: string[]
}

interface NavItem {
  value: number
  code?: number
  submodule: { code?: SubmoduleCode; name?: string }[]
  label?: string
}

export const useNotify = ({ navMaps }: TNotifyParams) => {
  const [activeTabs, updateActiveTabs] = useState([0, 0])
  const [submoduleId, setSubmoduleId] = useState(0)
  const { loginData } = useAuthenticate()
  const [traderUid, setTraderUid] = useState('')
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const fetchingRef = useRef(false) // 添加一个ref来跟踪是否正在获取数据

  // 获取查询客户端
  const queryClient = useQueryClient()

  // 首先获取mutation函数
  const { mutateAsync: getMessageList } = usePostV2InmailMessageListMutation()

  // 创建一个intersectionObserver hook来检测底部元素是否可见
  const { ref: loadMoreRef, inView } = useInView()

  // 使用useInfiniteQuery替代原来的mutation调用
  const {
    data: infiniteMessages,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
  } = useInfiniteQuery<PageData, Error, InfiniteData<PageData>, [string, number, number], number>({
    queryKey: ['messages', activeTabs[0], submoduleId],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = {
        moduleId: NAV_LIST[activeTabs[0]]?.code,
        submoduleId: submoduleId || undefined,
        pageSize: 10,
        pageNum: pageParam as number,
      }

      const res = await getMessageList(params)

      return {
        data: (res?.data?.data || []).map((item) => ({
          ...item,
          hasLink: !!item.bizType && !NO_LINK_TYPES.includes(item.bizType),
          createdTime: Number(item.createdTime),
        })),
        totalCount: res?.data?.totalCount || 0,
        pageNum: pageParam as number,
        pageSize: 10,
        nextPage: (pageParam as number) + 1,
      }
    },
    getNextPageParam: (lastPage) => {
      const loadedItemsCount = lastPage.pageNum * lastPage.pageSize
      if (loadedItemsCount < lastPage.totalCount) {
        return lastPage.nextPage
      }
      return undefined
    },
    enabled: !!loginData.isLogin,
  })

  // 监听tab和二级分类的变化，重置数据并重新获取
  useEffect(() => {
    // 当tab或二级分类变化时，重置查询状态并重新获取数据
    queryClient
      .resetQueries({
        queryKey: ['messages', activeTabs[0], submoduleId],
        exact: true,
      })
      .then(() => {
        // 重置后立即重新获取数据
        refetch()
      })
  }, [activeTabs[0], submoduleId, queryClient, refetch])

  // 当用户滚动到底部时加载更多 - 优化加载逻辑
  useEffect(() => {
    // 如果不在视图中，或者没有更多数据，或者正在获取中，或者已经在加载更多，则直接返回
    if (!inView || !hasNextPage || isFetching || isLoadingMore || fetchingRef.current) {
      return
    }

    // 设置正在获取标志，防止重复触发
    fetchingRef.current = true

    // 设置正在加载更多标志
    setIsLoadingMore(true)

    // 加载下一页数据
    fetchNextPage().finally(() => {
      setIsLoadingMore(false)
      // 延迟重置fetchingRef，防止快速连续触发
      setTimeout(() => {
        fetchingRef.current = false
      }, 300)
    })
  }, [inView, hasNextPage, isFetching, fetchNextPage, isLoadingMore])

  // 整合所有页的数据
  const messageListData = useMemo(() => {
    if (!infiniteMessages?.pages?.length) {
      return {
        list: [],
        totalCount: 0,
        pageNum: 1,
        pageSize: 10,
      }
    }

    const allMessages = infiniteMessages.pages.flatMap((page: PageData) => page.data)
    const lastPage = infiniteMessages.pages[infiniteMessages.pages.length - 1]

    return {
      list: allMessages,
      totalCount: lastPage.totalCount,
      pageNum: lastPage.pageNum,
      pageSize: lastPage.pageSize,
    }
  }, [infiniteMessages])

  // 获取未读消息数量
  const { data: unReadCountData } = useGetV2InmailMessageUnReadCountQuery(
    {},
    {
      enabled: loginData.isLogin,
    }
  )

  // 获取消息分类
  const { data: classifyData } = useGetV2InmailMessageClassifyQuery(
    {},
    {
      enabled: loginData.isLogin,
    }
  )

  // 获取新手任务
  const { data: rewardTasksData } = useGetRewardTasksListQuery(
    {
      pageSize: 1000,
      bver: 3,
    },
    {
      enabled: loginData.isLogin,
    }
  )

  // 判断是否有新手任务
  const hasNewTask = useMemo(() => {
    if (!rewardTasksData?.data?.data?.newbieTasks?.length) {
      return false
    }
    return rewardTasksData.data?.data?.newbieTasks.some((task) => task.receiveStatus === 0)
  }, [rewardTasksData])

  // 获取合约跟单状态
  const { refetch: refetchTraderStatus } = useGetV1PerpetualFollowsPublicTraderCollectQuery(
    { traderUid }, // 初始参数为空
    {
      enabled: false, // 默认不执行查询
    }
  )

  // 更新消息已读状态
  const { mutate: updateMessageReadStatus } = usePostV2InmailMessageStatusMutation()

  // 消息分类处理
  const messages = useMemo(() => {
    if (!messageListData?.list?.length) {
      return { today: [], earlier: [] }
    }

    const todayStart = new Date().setHours(0, 0, 0, 0)
    const today: NotifyMessage[] = []
    const earlier: NotifyMessage[] = []

    messageListData.list.forEach((item: NotifyMessage) => {
      if (item.createdTime && item.createdTime >= todayStart) {
        today.push(item)
      } else {
        earlier.push(item)
      }
    })

    return { today, earlier }
  }, [messageListData])

  // 处理消息导航
  const handleMessageNavigation = async (message: NotifyMessage) => {
    let path = ''

    // 公告消息跳转
    if (message.moduleCode === ModuleCode.AnnouncementMessage) {
      if (!message.jumpLink && message.supportCode) {
        path = `/support/article?id=${message.id}&code=${message.supportCode}`
      } else if (message.jumpLink) {
        window.open(message.jumpLink)
        return
      }
      let tradeData: { instType: InstType; contractCode?: string; tradeId?: string }

      switch (message.bizType) {
        case BizType.OpenTradeNotification:
          try {
            tradeData = JSON.parse(message.dataJson || '{}')
            if (tradeData.instType == InstType.Perpetual) {
              path = `/contract/exchange/trade/?tradeName=${tradeData.contractCode?.toLocaleLowerCase()}`
            } else if (tradeData.instType == InstType.Spot) {
              const tradeInfo = await getV3GetOneTradeInfo({ tradeId: tradeData.tradeId })
              const tradeCode =
                tradeInfo?.data?.data?.sellSymbol?.toLocaleLowerCase() +
                '_' +
                tradeInfo?.data?.data?.buySymbol?.toLocaleLowerCase()
              path = `/trade/exchange/?tradeCode=${tradeCode}`
            }
          } catch (e) {
            console.error('Failed to parse live data', e)
          }
          break
      }
    }

    // 系统通知跳转
    if (message.moduleCode === ModuleCode.SystemNotification) {
      let jsonData: { cardId: string; fundId: string }
      let cashbackData: { launchId: string }
      let liveData: { liveRoomId: string }

      switch (message.bizType) {
        case BizType.UserRegisterSuccess:
        case BizType.UserLoginSuccess:
        case BizType.BenefitsCenterHome:
          path = '/benefitsCenter/home'
          break
        case BizType.RealNameAuthSuccess:
        case BizType.BenefitsCenterTask:
          path = '/benefitsCenter/myTask'
          break
        case BizType.RealNameAuthFailed:
        case BizType.SystemUpdate:
        case BizType.BenefitsCenterReward:
        case BizType.BenefitsCenterMyReward:
          path = '/benefitsCenter/myReward'
          break
        case BizType.AccountInfoModified:
        case BizType.AccountCenter:
          path = '/user/personSetting'
          break
        case BizType.DepositProcessing:
        case BizType.DepositSuccess:
        case BizType.RiskDepositFrozen:
          path = '/assetManagement/financial?type=1'
          break
        case BizType.WithdrawalSuccess:
          path = '/assetManagement/financial?type=2'
          break
        case BizType.BackendRewardDistribution:
          path = '/assetManagement/financial?type=4'
          break
        case BizType.TradingFundRelated1:
        case BizType.TradingFundRelated2:
        case BizType.TradingFundRelated3:
        case BizType.TradingFundRelated4:
          try {
            jsonData = JSON.parse(message.dataJson || '{}') as { cardId: string; fundId: string }
            path = `/benefitsCenter/tradingFundDetails?cardId=${jsonData.cardId}&fundId=${jsonData.fundId}&cardStatus=4`
          } catch (e) {
            console.error('Failed to parse trading fund data', e)
          }
          break
        case BizType.CashbackActivity:
          path = `/activity/cashback`
          break
        case BizType.CashbackDetail1:
        case BizType.CashbackDetail2:
        case BizType.CashbackDetail3:
        case BizType.CashbackDetail4:
          try {
            cashbackData = JSON.parse(message.dataJson || '{}') as { launchId: string }
            path = `/activity/cashbackDetails?launchId=${cashbackData.launchId}`
          } catch (e) {
            console.error('Failed to parse cashback data', e)
          }
          break
        case BizType.LiveBroadcastUpcoming:
          try {
            liveData = JSON.parse(message.dataJson || '{}') as { liveRoomId: string }
            path = `/live/room?id=${liveData.liveRoomId}`
          } catch (e) {
            console.error('Failed to parse live data', e)
          }
          break
        // ...more cases
      }
    }

    // 行情通知跳转
    if (message.moduleCode === ModuleCode.Market) {
      let jsonData: { baseCurrency: string; quoteCurrency: string }
      try {
        jsonData = JSON.parse(message.dataJson || '{}') as { baseCurrency: string; quoteCurrency: string }
        const tradeCode = jsonData.baseCurrency?.toLocaleLowerCase() + '_' + jsonData.quoteCurrency?.toLocaleLowerCase()
        path = `/trade/exchange/?tradeCode=${tradeCode}`
      } catch (e) {
        console.error('Failed to parse market data', e)
      }
    }

    // 交易通知跳转
    if (message.moduleCode === ModuleCode.Trade) {
      let tradeData: { instType: InstType; contractCode?: string; tradeId?: string }
      try {
        tradeData = JSON.parse(message.dataJson || '{}')
        if (tradeData.instType == InstType.Perpetual) {
          path = `/contract/exchange/trade/?tradeName=${tradeData.contractCode?.toLocaleLowerCase()}`
        } else if (tradeData.instType == InstType.Spot) {
          const tradeInfo = await getV3GetOneTradeInfo({ tradeId: tradeData.tradeId })
          const tradeCode =
            tradeInfo?.data?.data?.sellSymbol?.toLocaleLowerCase() +
            '_' +
            tradeInfo?.data?.data?.buySymbol?.toLocaleLowerCase()
          path = `/trade/exchange/?tradeCode=${tradeCode}`
        }
      } catch (e) {
        console.error('Failed to parse live data', e)
      }
    }

    // 合约跟单相关跳转
    if (message.submoduleCode === ModuleCode.ContractCopy || message.submoduleCode === ModuleCode.ContractWarning) {
      const jsonObj = JSON.parse(message.dataJson || '{}')

      // 带单相关消息
      if (message.bizType === BizType.OrderBearer) {
        const traderUid = JSON.parse(jsonObj.extras).traderUid
        setTraderUid(() => traderUid)
        // 使用 refetch 来获取最新状态
        const { data: status } = await refetchTraderStatus()

        if (status?.tradeStatus === 1) {
          // 带单中
          path = `/chargeBack/OrderBearer/homepage?id=${traderUid}`
        } else {
          // 暂停/取消带单
          path = '/chargeBack'
        }
      } else if (
        message.bizType === BizType.OpenPositionFailed ||
        message.bizType === BizType.OpenPositionSuccess ||
        message.bizType === BizType.ClosePositionSuccess
      ) {
        // 开仓失败/开仓成功/平仓成功
        path = '/chargeBack/orderDocumentary/myDocumentary?tab=1'
      } else if (
        message.bizType === BizType.CopyTraderPause ||
        message.bizType === BizType.TraderProfitRatioAdjustment ||
        message.bizType === BizType.TraderDisqualified
      ) {
        // 跟单员暂停带单/交易员分润比例调整/带单-交易员被取消资格
        path = '/chargeBack/orderDocumentary/myDocumentary?tab=3'
      } else if (message.bizType === BizType.TraderRemoved) {
        // 交易员移除
        path = '/chargeBack/orderDocumentary/myDocumentary?tab=4'
      } else if (message.bizType === BizType.TriggerTakeProfitStopLoss) {
        // 触发止盈/止损
        path = '/chargeBack/orderDocumentary/myDocumentary?tab=2'
      } else if (
        message.bizType === BizType.TraderUpgradeSuccess ||
        message.bizType === BizType.TraderUpgradeFailed ||
        message.bizType === BizType.CopyTradeSuccess ||
        message.bizType === BizType.CopyTradeApplicationRejected
      ) {
        // 交易员升级成功/交易员升级失败/跟单员跟单成功/带单申请审核不通过
        path = `/chargeBack/OrderBearer/myBearer?id=${loginData.fid}`
      } else if (
        message.bizType === BizType.TraderDisqualifiedAgain ||
        message.bizType === BizType.CopyTradeApplicationApproved
      ) {
        // 交易员被取消资格/带单申请审核通过
        path = `/chargeBack`
      }
    }

    return path
  }

  // 处理消息点击
  const handleMessageClick = async (message: NotifyMessage) => {
    if (!message.read && message.id) {
      await markAsRead(message.id)
      // 设置已读状态
      message.read = 1
      if (unReadCountData?.data?.data?.countTotal && unReadCountData?.data?.data?.countTotal > 0) {
        unReadCountData.data.data.countTotal = unReadCountData?.data?.data?.countTotal - 1
      }
    }

    const path = await handleMessageNavigation(message)
    const url = path?.startsWith('http') ? path : `/${getLangFromUrl()}${path}`
    if (path) {
      window.open(url, '_blank')
    }
  }

  // 标记消息已读
  const markAsRead = async (messageId: string) => {
    await updateMessageReadStatus({
      messageId: messageId,
      rangeType: 1,
    })
  }

  // 全部已读
  const markAllAsRead = async () => {
    await updateMessageReadStatus({
      rangeType: 0,
    })

    if (unReadCountData?.data?.data?.countTotal && unReadCountData?.data?.data?.countTotal > 0) {
      unReadCountData.data.data.countTotal = 0
    }
  }

  // 处理导航数据
  const navList = NAV_LIST.map((nav) => {
    const matchedNav = classifyData?.data?.data?.find((i: { code?: number }) => i.code === nav.code)
    return {
      ...nav,
      label: navMaps[nav.value],
      submodule: matchedNav?.submodule || [],
    } as NavItem
  })

  return {
    // 导航相关
    navList,
    activeTabs,
    updateActiveTabs,

    // 消息相关
    messages,
    isLoading,
    isFetchingMore: (isFetching && !isLoading) || isLoadingMore, // 更新加载状态指示器
    hasNextPage, // 添加是否有更多页的状态
    loadMoreRef, // 导出ref用于绑定到底部加载元素
    unReadCountTotal: unReadCountData?.data?.data?.countTotal,

    // 二级分类相关
    submoduleId,
    setSubmoduleId,

    // 消息操作方法
    markAsRead,
    markAllAsRead,

    hasNewTask,
    handleMessageClick,
    handleMessageNavigation,

    // 常量
    NO_LINK_TYPES,
    LABEL_LIST,
  }
}
